// AI generated

import { useDebouncedCallback } from '@mantine/hooks'
import type { Edge, Node, ReactFlowInstance } from '@xyflow/react'
import { useCallback, useEffect, useState } from 'react'
import { Transforms } from 'slate'
import { ReactEditor, useSlate } from 'slate-react'
import type { FlowElement } from '..'
import { SAVE_DEBOUNCE_MS } from '../constants'
import { sortNodes } from '../lib/changes'
import { extractEdgeMarkers } from '../lib/markers'

/**
 * Selection is per-user and never travels through the shared document, so an
 * element arriving from Slate takes its `selected` from the local copy.
 */
const localSelection = (
  local: { id: string; selected?: boolean }[],
  id: string,
) => local.find((item) => item.id === id)?.selected ?? false

export function useFlowState(element: FlowElement) {
  const editor = useSlate()
  const [nodes, nativeSetNodes] = useState<Node[]>(() =>
    [...element.nodes].sort(sortNodes),
  )
  const [edges, nativeSetEdges] = useState<Edge[]>(() =>
    element.edges.map((edge) => extractEdgeMarkers(edge)),
  )
  const [rfInstance, setRfInstance] = useState<ReactFlowInstance | null>(null)

  // Update on external change
  useEffect(() => {
    nativeSetNodes((current) =>
      element.nodes
        .map((remoteNode) => ({
          ...remoteNode,
          selected: localSelection(current, remoteNode.id),
        }))
        .sort(sortNodes),
    )
  }, [element.nodes])

  useEffect(() => {
    nativeSetEdges((current) =>
      element.edges.map((remoteEdge) => ({
        ...extractEdgeMarkers(remoteEdge),
        selected: localSelection(current, remoteEdge.id),
      })),
    )
  }, [element.edges])

  const saveFlowInSlate = useDebouncedCallback(() => {
    if (!rfInstance) return

    const path = ReactEditor.findPath(editor, element)

    if (!path) return

    const flow = rfInstance.toObject()

    Transforms.setNodes(
      editor,
      {
        // force `selected: false` so per-user selection stays out of the shared doc
        nodes: flow.nodes.map((n) => ({ ...n, selected: false })),
        edges: flow.edges.map((e) => ({ ...e, selected: false })),
        viewport: flow.viewport,
      },
      { at: path },
    )
  }, SAVE_DEBOUNCE_MS)

  const setNodes = useCallback(
    (nodes: Node[]) => {
      nativeSetNodes([...nodes].sort(sortNodes))
      saveFlowInSlate()
    },
    [saveFlowInSlate],
  )

  const setEdges = useCallback(
    (edges: Edge[]) => {
      nativeSetEdges(edges.map((e) => extractEdgeMarkers(e)))
      saveFlowInSlate()
    },
    [saveFlowInSlate],
  )

  return { nodes, edges, setNodes, setEdges, rfInstance, setRfInstance }
}
